<template lang="pug">
div
  .carousel-scroll(
    v-if="stableItems.length"
    ref="scrollEl"
    :class="{ 'is-dragging': isDragging }"
    @mousedown="onMouseDown"
    @click.capture="onCarouselClick"
  )
    carousel-card(
      v-for="(item, i) in stableItems"
      :key="i"
      :game="game"
      :component="component"
      :item="item"
      :item-index="i"
      :selected="i === selectedIndex"
      :paused="paused"
      @select="$emit('select', i)"
      @rendered="onCardRendered"
    )
  .pa-4.grey--text(v-else) No items to preview. Make sure a spreadsheet and worksheet are linked.
</template>

<script>
import { mapGetters } from 'vuex'
import CarouselCard from './CarouselCard.vue'

function itemsEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false
  return a.every((field, i) => field.value === b[i].value)
}

export default {
  props: ['game', 'component', 'selectedIndex', 'paused'],

  components: { CarouselCard },

  data() {
    return {
      isDragging: false,
      renderTotal: 0,
      renderDone: 0,
      stableItems: [],
    }
  },

  created() {
    this._dragStartX = 0
    this._dragScrollLeft = 0
    this._didDrag = false
  },

  mounted() {
    this._onMouseMove = (e) => this.onMouseMove(e)
    this._onMouseUp = () => this.onMouseUp()
    window.addEventListener('mousemove', this._onMouseMove)
    window.addEventListener('mouseup', this._onMouseUp)
  },

  beforeDestroy() {
    window.removeEventListener('mousemove', this._onMouseMove)
    window.removeEventListener('mouseup', this._onMouseUp)
  },

  computed: {
    ...mapGetters(['getComponentItems']),

    renderProgress() {
      if (this.renderTotal === 0) return 100
      return Math.min(100, Math.round((this.renderDone / this.renderTotal) * 100))
    },

    items() {
      if (this.component && this.component.spreadsheetId && this.component.worksheetId) {
        void this.$store.state.cache.cache[`${this.component.spreadsheetId}-${this.component.worksheetId}`]
      }
      return this.component ? this.getComponentItems(this.component) : []
    }
  },

  watch: {
    selectedIndex(index) {
      this.$nextTick(() => this.scrollToCard(index))
    },

    items: {
      immediate: true,
      handler(newItems) {
        const prev = this.stableItems

        if (newItems.length !== prev.length) {
          // Row added or deleted — full re-render of all cards
          this.stableItems = newItems
          this.renderTotal = newItems.length
          this.renderDone = 0
          if (newItems.length > 0) this.$emit('progress', 0)
          return
        }

        // Preserve references for unchanged items so their TemplateRenderers
        // don't re-render. Only items whose cell values actually changed get a
        // new reference, triggering a targeted re-render.
        let changedCount = 0
        const next = newItems.map((newItem, i) => {
          if (itemsEqual(newItem, prev[i])) return prev[i]
          changedCount++
          return newItem
        })

        this.stableItems = next
        if (changedCount > 0 && this.renderDone >= this.renderTotal) {
          // Only start a new progress cycle when nothing is currently rendering.
          // Mid-render cell edits re-render the affected card silently without
          // resetting the counter for the cards still in the original batch.
          this.renderTotal = changedCount
          this.renderDone = 0
          this.$emit('progress', 0)
        }
      }
    },

    renderProgress(val) {
      this.$emit('progress', val)
    },
  },

  methods: {
    onMouseDown(e) {
      if (e.button !== 0) return
      this.isDragging = true
      this._didDrag = false
      this._dragStartX = e.clientX
      this._dragScrollLeft = this.$refs.scrollEl.scrollLeft
    },

    onMouseMove(e) {
      if (!this.isDragging) return
      const dx = e.clientX - this._dragStartX
      if (Math.abs(dx) > 4) this._didDrag = true
      this.$refs.scrollEl.scrollLeft = this._dragScrollLeft - dx
    },

    onMouseUp() {
      if (!this.isDragging) return
      this.isDragging = false
    },

    onCarouselClick(e) {
      if (this._didDrag) {
        e.stopPropagation()
        this._didDrag = false
      }
    },

    reset() {
      const items = this.items
      this.stableItems = []
      this.$nextTick(() => {
        this.stableItems = items
        this.renderTotal = items.length
        this.renderDone = 0
        if (items.length > 0) this.$emit('progress', 0)
      })
    },

    onCardRendered() {
      this.renderDone = Math.min(this.renderTotal, this.renderDone + 1)
    },

    scrollToCard(index) {
      const el = this.$refs.scrollEl
      if (!el) return
      const card = el.children[index]
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }
}
</script>

<style scoped>
.carousel-scroll {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  padding: 36px 8px 12px;
  gap: 0;
  align-items: flex-start;
  cursor: grab;
}
.carousel-scroll.is-dragging {
  cursor: grabbing;
  user-select: none;
}
.carousel-scroll::-webkit-scrollbar { height: 6px; }
.carousel-scroll::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(128,128,128,0.4);
}
</style>
